import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ReportIssueWidget } from '../components/ReportIssueWidget';
import QuickActionFAB from '../components/QuickActionFAB';
import RolePreviewBanner from '../components/RolePreviewBanner';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyFilter } from '../contexts/CompanyFilterContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LayoutDashboard,
  ArrowLeftRight,
  AlertTriangle,
  Banknote,
  Receipt,
  Monitor,
  MapPin,
  Gamepad2,
  Users,
  Truck,
  UserPlus,
  Settings,
  LogOut,
  Menu,
  X,
  UserCog,
  Building2,
  Package,
  FileBox,
  DollarSign,
  Route,
  Sun,
  Moon,
  Handshake,
  Shield,
  ShieldAlert,
  CreditCard,
  Lock,
  Activity,
  BarChart3,
  User,
  BookOpen,
  HelpCircle,
  CalendarCheck,
  Server,
  Eye,
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import SidebarNavItem from './SidebarNavItem';
import SubscriptionTierBadge from './SubscriptionTierBadge';

// Theme Toggle Component
const ThemeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg hover:bg-gray-800/30 transition-colors"
      data-testid="theme-toggle"
      title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    >
      {isDarkMode ? (
        <Sun className="w-5 h-5 text-amber-400" />
      ) : (
        <Moon className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );
};

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const { 
    companies, 
    selectedCompanyId, 
    setSelectedCompanyId, 
    selectedCompany, 
    isSuperAdmin,
    canShowAllOption,
    showCompanyDropdown
  } = useCompanyFilter();
  const { canView, loading: permissionsLoading, effectiveRole, isPreviewActive, canUsePreview, availableRoles, startPreview, stopPreview, previewRole } = usePermissions();
  const { t } = useTranslation();
  const { canAccess, loading: subscriptionLoading, getUpgradeInfo } = useSubscription();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to check view permission (allow all during loading)
  const checkCanView = (page) => permissionsLoading ? true : canView(page);
  
  // Helper function to check subscription access (allow all during loading)
  const checkCanAccess = (feature) => subscriptionLoading ? true : canAccess(feature);

  // Combined check: permission AND subscription
  const checkAccess = (page, feature) => {
    return checkCanView(page) && checkCanAccess(feature || page);
  };

  // Enterprise-only features that should show as locked for lower tiers
  const ENTERPRISE_FEATURES = ['invoices', 'routes', 'companies', 'multi_company'];

  // Check if we should show a feature as locked (has permission but not subscription)
  const isFeatureLocked = (feature) => {
    if (user?.role === 'super_admin' || user?.role === 'demo_admin') return false;
    if (subscriptionLoading) return false;
    return ENTERPRISE_FEATURES.includes(feature) && !checkCanAccess(feature);
  };

  // Navigation items - gated by role permissions only (subscription used for locked indicators)
  const listItems = [
    { to: '/dashboard/money-in-out', icon: ArrowLeftRight, label: t('nav.moneyInOut'), page: 'money_in_out' },
    { to: '/dashboard/cash-handoffs', icon: Banknote, label: 'Cash Handoffs', page: 'money_in_out' },
    { to: '/dashboard/issues', icon: AlertTriangle, label: t('nav.issues'), page: 'issues' },
    { to: '/dashboard/expenses', icon: Receipt, label: t('nav.expenses'), page: 'expenses' },
    { to: '/dashboard/locations', icon: MapPin, label: t('nav.locations'), page: 'locations' },
    { to: '/dashboard/location-managers', icon: Users, label: t('nav.locationManagers'), page: 'location_managers' },
    { to: '/dashboard/referers', icon: UserPlus, label: t('nav.referrers'), page: 'referers' },
  ].filter(item => checkCanView(item.page));

  // Business items: show enterprise features as locked instead of hidden
  const businessItems = [
    { to: '/dashboard/inventory', icon: Package, label: t('nav.inventory'), page: 'inventory', feature: 'inventory' },
    { to: '/dashboard/invoices', icon: FileBox, label: t('nav.invoices'), page: 'invoices', feature: 'invoices' },
    { to: '/dashboard/vendors', icon: Truck, label: t('nav.vendors'), page: 'vendors', feature: 'vendors' },
    { to: '/dashboard/routes', icon: Route, label: t('nav.routesMaps'), page: 'routes', feature: 'routes' },
  ].map(item => ({
    ...item,
    locked: isFeatureLocked(item.feature),
  })).filter(item => checkCanView(item.page));

  const reportItems = [
    { to: '/dashboard/reports/machine-accounting', icon: ArrowLeftRight, label: t('nav.machineAccounting'), page: 'machine_accounting' },
    { to: '/dashboard/reports/partner-payouts', icon: DollarSign, label: t('nav.partnerPayouts'), page: 'partner_payouts' },
    { to: '/dashboard/reports/tax', icon: Receipt, label: t('nav.taxReport'), page: 'machine_accounting' },
    { to: '/dashboard/reports/true-pnl', icon: BarChart3, label: t('nav.truePnl'), page: 'machine_accounting' },
    { to: '/dashboard/reports/referrer-payouts', icon: UserPlus, label: t('nav.referrerPayouts'), page: 'machine_accounting' },
  ].filter(item => checkCanView(item.page));

  const NavItem = (props) => (
    <SidebarNavItem {...props} onNavigate={() => setSidebarOpen(false)} />
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-gray-900 border-b border-gray-800 px-4 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
            <Gamepad2 className="w-4 h-4 text-black" />
          </div>
          <span className="font-bold text-amber-500">SlotFleet</span>
        </div>
        {/* Mobile User Menu */}
        <div className="flex items-center gap-1 text-gray-300">
          <QuickActionFAB />
          <LanguageSwitcher />
          <NotificationBell />
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400"
                data-testid="mobile-user-menu"
              >
                <User className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'demo_admin') && (
                <DropdownMenuItem 
                  onClick={() => navigate('/dashboard/security')} 
                  className="cursor-pointer"
                  data-testid="mobile-security-settings-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Security Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Sidebar Overlay for Mobile - Click to close */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-200 ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`fixed top-14 lg:top-0 left-0 z-50 h-[calc(100vh-3.5rem)] lg:h-screen w-56 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo - Hidden on mobile since we have mobile header */}
          <NavLink to="/dashboard" className="hidden lg:flex h-14 items-center gap-2 px-4 border-b border-border flex-shrink-0 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-black" />
            </div>
            <span className="font-bold text-xl text-amber-500">SlotFleet</span>
          </NavLink>

          {/* Navigation - Scrollable area */}
          <div className="flex-1 overflow-y-auto py-2 pb-6" data-testid="sidebar-nav-scroll">
            <nav className="px-2">
              {/* Dashboard - Always visible */}
              {checkCanView('dashboard') && (
                <NavLink
                  to="/dashboard"
                  end
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? 'active' : ''}`
                  }
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Dashboard</span>
                </NavLink>
              )}

              {/* LISTS Section */}
              {listItems.length > 0 && (
                <>
                  <div className="sidebar-section-title">{t('nav.lists')}</div>
                  {listItems.map((item) => (
                    <NavItem key={item.to} {...item} />
                  ))}
                </>
              )}

              {/* Partners - in lists section if has access */}
              {checkCanView('partners') && (
                <NavItem to="/dashboard/partners" icon={Handshake} label="Partners" />
              )}

              {/* BUSINESS Section */}
              {businessItems.length > 0 && (
                <>
                  <div className="sidebar-section-title">{t('nav.business')}</div>
                  {businessItems.map((item) => (
                    <NavItem key={item.to} {...item} />
                  ))}
                </>
              )}

              {/* ANALYTICS Section */}
              {checkCanView('analytics') && (
                <>
                  <div className="sidebar-section-title">ANALYTICS</div>
                  <NavItem to="/dashboard/analytics" icon={BarChart3} label="Analytics" />
                </>
              )}

              {/* REPORTS Section */}
              {reportItems.length > 0 && (
                <>
                  <div className="sidebar-section-title">REPORTS</div>
                  <NavItem to="/dashboard/reports/accounting-periods" icon={CalendarCheck} label="Accounting Periods" />
                  <NavItem to="/dashboard/reports" icon={BarChart3} label={t('nav.reportsPage')} end />
                  {reportItems.map((item) => (
                    <NavItem key={item.to} {...item} />
                  ))}
                </>
              )}

              {/* ADMINISTRATION Section */}
              <div className="sidebar-section-title">{t('nav.administration')}</div>
              {checkCanView('users') && (
                <NavItem to="/dashboard/users" icon={UserCog} label={t('nav.userManagement')} />
              )}
              {checkCanView('companies') && (
                <NavItem to="/dashboard/companies" icon={Building2} label={t('nav.companies')} locked={isFeatureLocked('companies')} />
              )}
              {checkCanView('role_permissions') && (
                <NavItem to="/dashboard/role-permissions" icon={Shield} label={t('nav.rolePermissions')} />
              )}
              {checkCanView('activity_logs') && (
                <NavItem to="/dashboard/audit-trail" icon={Activity} label={t('nav.auditTrail')} />
              )}
              {(effectiveRole === 'super_admin' || effectiveRole === 'admin' || effectiveRole === 'demo_admin') && (
                <NavItem to="/dashboard/security" icon={Lock} label="Security" />
              )}
              {checkCanView('subscription') && (
                <NavItem to="/dashboard/subscription" icon={CreditCard} label="Subscription" />
              )}
              <NavItem to="/dashboard/getting-started" icon={BookOpen} label="Getting Started" />
              <NavItem to="/dashboard/help" icon={HelpCircle} label="Help Center" />

              {/* OPERATIONS Section - Super Admin only */}
              {effectiveRole === 'super_admin' && (
                <>
                  <div className="sidebar-section-title">OPERATIONS</div>
                  <NavItem to="/dashboard/leads" icon={Users} label="Leads" />
                  <NavItem to="/dashboard/plan-management" icon={Settings} label="Plan Management" />
                  <NavItem to="/dashboard/extra-location-billing" icon={DollarSign} label="Extra Location Billing" />
                  <NavItem to="/dashboard/visitor-analytics" icon={BarChart3} label="Visitor Analytics" />
                  <NavItem to="/dashboard/production-monitor" icon={Monitor} label="Production Monitor" />
                  <NavItem to="/dashboard/security-audit" icon={ShieldAlert} label="Security Audit" />
                  <NavItem to="/dashboard/system-status" icon={Server} label="System Status" />
                </>
              )}
            </nav>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-56 pt-14 lg:pt-0 min-h-screen flex flex-col transition-colors duration-200 bg-background">
        {/* Top Bar */}
        <header className="hidden lg:flex h-14 border-b items-center justify-between px-6 flex-shrink-0 transition-colors duration-200 bg-card border-border">
          <div className="flex items-center gap-4">
            {/* Global Company Filter - Only show for users with multiple companies */}
            {showCompanyDropdown ? (
              <div className="flex items-center gap-2 rounded-lg px-3 py-1.5 border bg-secondary border-border">
                <Building2 className="w-4 h-4 text-amber-600" />
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger className="border-0 bg-transparent shadow-none h-auto p-0 min-w-[180px] focus:ring-0" data-testid="global-company-filter">
                    <SelectValue placeholder="Select Company">
                      {companies.find(c => c.id === selectedCompanyId)?.name || 
                       (selectedCompanyId === 'all' && canShowAllOption ? 'All Companies' : 
                        companies.length === 1 ? companies[0].name : 'Select Company')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {canShowAllOption && (
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">All Companies</span>
                        </div>
                      </SelectItem>
                    )}
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-amber-500" />
                          <span>{company.name}</span>
                          <SubscriptionTierBadge tier={company.subscription_tier} />
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : companies.length === 1 ? (
              /* Single company badge - no dropdown needed */
              <div className="flex items-center gap-2 rounded-lg px-3 py-1.5 border bg-secondary border-border">
                <Building2 className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-foreground">{companies[0].name}</span>
                <SubscriptionTierBadge tier={companies[0].subscription_tier} />
              </div>
            ) : null}
            
            <div className="h-6 w-px bg-border" />
          </div>
          <div className="flex items-center gap-4">
            {selectedCompany && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                Filtering: {selectedCompany.name}
              </span>
            )}
            <QuickActionFAB />
            <LanguageSwitcher />
            <NotificationBell />
            <ReportIssueWidget userRole={user?.role} />
            {canUsePreview && user?.role === 'super_admin' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isPreviewActive
                        ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                        : 'hover:bg-secondary text-muted-foreground'
                    }`}
                    data-testid="role-preview-trigger"
                  >
                    <Eye className="w-4 h-4" />
                    {isPreviewActive ? (previewRole?.replace(/_/g, ' ')) : 'View as'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {isPreviewActive && (
                    <DropdownMenuItem
                      onClick={stopPreview}
                      className="cursor-pointer text-violet-600 font-medium"
                      data-testid="stop-preview-option"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Exit Preview
                    </DropdownMenuItem>
                  )}
                  {availableRoles
                    .filter(r => r !== (user?.role))
                    .map(role => (
                      <DropdownMenuItem
                        key={role}
                        onClick={() => startPreview(role)}
                        className={`cursor-pointer capitalize ${previewRole === role ? 'bg-violet-50 dark:bg-violet-900/20' : ''}`}
                        data-testid={`preview-role-${role}`}
                      >
                        <User className="w-4 h-4 mr-2" />
                        {role.replace(/_/g, ' ')}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-2 py-1 hover:bg-secondary" data-testid="user-menu-trigger">
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                    <p className="text-xs capitalize text-muted-foreground">{(effectiveRole || user?.role)?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-black font-medium">
                    {user?.name?.charAt(0)}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'demo_admin') && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard/security')} 
                    className="cursor-pointer"
                    data-testid="security-settings-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Security Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        
        <RolePreviewBanner />

        {/* Mobile Company Filter - Only show for users with multiple companies */}
        {showCompanyDropdown ? (
          <div className="lg:hidden border-b px-4 py-2 flex-shrink-0 bg-card border-border">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger className="flex-1 h-9 text-sm" data-testid="mobile-company-filter">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  {canShowAllOption && (
                    <SelectItem value="all">All Companies</SelectItem>
                  )}
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      <span>{company.name}</span>
                      <SubscriptionTierBadge tier={company.subscription_tier} mobile />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : companies.length === 1 ? (
          /* Single company badge on mobile */
          <div className="lg:hidden border-b px-4 py-2 flex-shrink-0 bg-card border-border">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-foreground">{companies[0].name}</span>
              <SubscriptionTierBadge tier={companies[0].subscription_tier} mobile />
            </div>
          </div>
        ) : null}

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
